import React, { useState, useRef, useEffect } from 'react';
import './GameFilters.css';

/**
 * Shared sport filter tabs + search bar for HomePage and GamesPage.
 *
 * Props:
 * - sportFilters: [{ key, label }] — array of filter tabs (e.g. All Sports, NBA, NFL…)
 * - activeFilter: string — currently selected filter key
 * - onFilterChange: (key) => void
 * - searchQuery: string — current search text
 * - onSearchChange: (query) => void
 * - gameCount: number — total filtered games to display
 */
export default function GameFilters({
  sportFilters,
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  gameCount,
}) {
  const [searchOpen, setSearchOpen] = useState(!!searchQuery);
  const searchInputRef = useRef(null);

  // Auto-focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearchToggle = () => {
    if (searchOpen && searchQuery) {
      // Clear search when closing
      onSearchChange('');
    }
    setSearchOpen(!searchOpen);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="game-filters">
      {/* Search Bar */}
      <div className="search-row">
        {searchOpen ? (
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={handleClearSearch}>
                ✕
              </button>
            )}
            <button className="search-close" onClick={handleSearchToggle}>
              Cancel
            </button>
          </div>
        ) : (
          <button className="search-toggle" onClick={handleSearchToggle}>
            🔍 Search Teams
          </button>
        )}
      </div>

      {/* Sport Filter Tabs */}
      {sportFilters.length > 1 && (
        <div className="sport-filters">
          {sportFilters.map((filter) => (
            <button
              key={filter.key}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => onFilterChange(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      )}

      {/* Result count when searching */}
      {searchQuery && (
        <div className="search-results-count">
          {gameCount} {gameCount === 1 ? 'game' : 'games'} matching "{searchQuery}"
        </div>
      )}
    </div>
  );
}
